import { FORM_KEY_SEPARATOR } from '../workdayReferences';

/**
 * Turns the hire form's values into `create_hire`'s flat body.
 *
 * The form is nested under Workday's own section names; the request is flat.
 * The two are not mechanically related — `amount` appears three times under
 * different parents, and the parent is what decides which key it becomes — so
 * the section is always part of the lookup.
 */

/**
 * Blocks the payload nests. Everything else lands at the top level.
 *
 * Matched on a pattern rather than an exact section key: the block name is not
 * derivable from the section (`Request_Stock_Grant_Data` → `equity`), and the
 * sections arrive through the ordinary rendering loop, so a tenant on a
 * slightly different schema revision would silently flatten a block that has
 * to stay nested. Each entry names the thing, not the exact wrapper.
 */
const PAYLOAD_BLOCK_PATTERNS: [RegExp, string][] = [
  [/stock[_\s-]?grant/i, 'equity'],
  [/one[_\s-]?time[_\s-]?payment/i, 'one_time_payment'],
  [/allowance/i, 'allowance'],
];

const blockForSection = (sectionKey: string): string | undefined =>
  sectionKey
    ? PAYLOAD_BLOCK_PATTERNS.find(([pattern]) => pattern.test(sectionKey))?.[1]
    : undefined;

/**
 * `create_position` owns these; `create_hire` rejects them outright, so they
 * are dropped from the hire body even though the form collects them.
 */
const POSITION_ONLY_PAYLOAD_KEYS = new Set([
  'job_posting_title',
  'worker_type_id',
]);

/**
 * Identity keys Workday already holds for a returning applicant.
 *
 * Required only when `applicant_id` is absent. Sent alongside one they are at
 * best redundant and at worst contradict the record being rehired, so the
 * presence of the applicant is what removes them.
 */
const REHIRE_REDUNDANT_KEYS = new Set([
  'first_name',
  'last_name',
  'email',
  'country_code',
  'email_usage_type_id',
]);

/**
 * The contract-end set, sent only on a conversion.
 *
 * All three or none. The backend rejects a partial set outright, and rightly:
 * ending the contract and then failing the hire leaves someone neither
 * contractor nor employee, which only a Workday admin can unpick.
 */
/**
 * Where the naming rule needs the section to disambiguate.
 *
 * `Primary_Reason_Reference` would derive to `primary_reason_id`, which means
 * nothing on its own — it is the reason a *contract* ended, and only its
 * section says so. Matched on a pattern because the wrapper's exact name is
 * the backend's to choose.
 */
const SECTIONED_PAYLOAD_KEYS: [RegExp, string, string][] = [
  [/end[_\s-]?contract/i, 'primary_reason_id', 'end_contract_reason_id'],
];

const CONVERSION_KEYS = [
  'contingent_worker_id',
  'contract_end_date',
  'end_contract_reason_id',
] as const;

/**
 * Where the naming rule below does not hold.
 *
 * The equity and one-time-payment blocks used to need entries here; the
 * contract was renamed precisely so the rule covers them —
 * `Grant_Amount` → `grant_amount`, `Grant_Amount_Currency_Reference` →
 * `grant_amount_currency_id`, `One_Time_Payment_Plan_Reference` →
 * `one_time_payment_plan_id`. It also removed a real ambiguity: `equity.amount`
 * and `one_time_payment.amount` were two different amounts.
 */
const PAYLOAD_KEY_OVERRIDES: Record<string, string> = {
  Company_Assignments_Reference: 'company_id',
  Cost_Center_Assignments_Reference: 'cost_center_id',
  // Not an id at all — Workday sends the ISO country code.
  Country_Reference: 'country_code',
  Email_Address: 'email',
};

/**
 * The rule the confirmed rows follow: drop a trailing `_Reference` for `_id`,
 * then lower-case. `Hire_Reason_Reference` → `hire_reason_id`,
 * `Hire_Date` → `hire_date`, `Effective_Date` → `effective_date`.
 */
export const toPayloadKey = (schemaKey: string): string =>
  PAYLOAD_KEY_OVERRIDES[schemaKey] ??
  schemaKey.replace(/_Reference$/, '_Id').toLowerCase();

/**
 * What never reaches the request.
 *
 * `false` is deliberately NOT empty. Workday reads an unticked "Board
 * approved" as a grant that was put to the board and refused, which is a
 * different fact from one nobody raised — so an untouched checkbox has to
 * arrive as no key at all, and a ticked-then-unticked one as `false`.
 */
const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

/**
 * Builds the request body from the form's flat values.
 *
 * `equity` and `one_time_payment` are omitted entirely when the recruiter left
 * them empty — not null, not `{}`. The backend decides whether to run those
 * Workday steps purely from whether the key is present.
 */
export const buildHirePayload = (
  values: Record<string, unknown> = {}
): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  const blocks: Record<string, Record<string, unknown>> = {};

  // Decided up front: the identity keys are dropped by the *presence* of an
  // applicant, and `Object.entries` gives no ordering guarantee that would let
  // this be worked out as we go.
  const isRehire = Object.entries(values).some(
    ([formKey, value]) =>
      toPayloadKey(formKey.split(FORM_KEY_SEPARATOR).pop() as string) ===
        'applicant_id' && !isEmpty(value)
  );

  for (const [formKey, value] of Object.entries(values)) {
    if (isEmpty(value)) continue;

    const parts = formKey.split(FORM_KEY_SEPARATOR);
    const fieldKey = parts[parts.length - 1];
    const sectionKey = parts.length > 1 ? parts[0] : '';

    let payloadKey = toPayloadKey(fieldKey);
    const sectioned = SECTIONED_PAYLOAD_KEYS.find(
      ([pattern, from]) => payloadKey === from && pattern.test(sectionKey)
    );
    if (sectioned) payloadKey = sectioned[2];
    if (POSITION_ONLY_PAYLOAD_KEYS.has(payloadKey)) continue;
    if (isRehire && REHIRE_REDUNDANT_KEYS.has(payloadKey)) continue;

    const block = blockForSection(sectionKey);
    if (block) {
      blocks[block] = { ...(blocks[block] || {}), [payloadKey]: value };
    } else {
      payload[payloadKey] = value;
    }
  }

  for (const [block, contents] of Object.entries(blocks)) {
    if (Object.keys(contents).length) payload[block] = contents;
  }

  // Drop a half-filled contract-end set rather than let the server reject the
  // whole hire for it. A conversion the recruiter has not finished describing
  // is a normal hire, not a failed one.
  const conversionPresent = CONVERSION_KEYS.filter((key) => key in payload);
  if (
    conversionPresent.length > 0 &&
    conversionPresent.length < CONVERSION_KEYS.length
  ) {
    conversionPresent.forEach((key) => delete payload[key]);
  }

  return payload;
};
